import {
  Notification,
  Table,
  type TableProps,
} from '@omnipresentgroup/design-system';
import { useDocuments } from '@omnipresentgroup/domains';
import { DocumentAction, type DocumentItem } from '@omnipresentgroup/types';
import {
  type ColumnDef,
  type ColumnOrderState,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  type SortingState,
  type TableOptions,
  useReactTable,
} from '@tanstack/react-table';
import { type ReactElement, useMemo, useState } from 'react';

import { DocumentListColumnHeaders, useDocumentsTableConfig } from './hooks';

const NO_RIGHTS_MESSAGE =
  'You do not have permission to view the document list.';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const EMPTY_ARRAY: any[] = [];
const LOADING_ARRAY = Array.from({ length: 10 }, (_value, index) => ({
  id: index,
}));

const DEFAULT_SORT: {
  id: keyof typeof DocumentListColumnHeaders;
  desc: boolean;
}[] = [
  {
    id: 'createdAt',
    desc: true,
  },
];

type DocumentId =
  | {
      companyId: string;
      userId?: never;
    }
  | { userId: string; companyId?: never };

type Props = DocumentId & {
  columnVisibility: Partial<{
    [key in keyof typeof DocumentListColumnHeaders]: boolean;
  }>;
  columnOrderOverride?: (keyof typeof DocumentListColumnHeaders | string)[];
  optionsOverride?: Omit<TableOptions<DocumentItem>, 'data' | 'columns'>;
  columnsOverride?: ColumnDef<DocumentItem, unknown>[];
  emptyStateProps?: TableProps['emptyStateProps'];
  onSelectedRowsChange?: (selectedRowIds: string[]) => void;
  onRowClick?: (row: DocumentItem) => void;
  useRowSelection?: boolean;
  tableActions?: TableProps['tableActions'];
  showHeader?: boolean;
  filterSection?: ReactElement;
};

export const DocumentList = ({
  companyId,
  userId,
  columnVisibility,
  columnOrderOverride,
  optionsOverride,
  columnsOverride,
  emptyStateProps,
  onRowClick,
  useRowSelection,
  tableActions,
  showHeader = true,
  filterSection,
}: Props) => {
  const {
    data: documentsInfo,
    isLoading,
    isError,
  } = useDocuments({
    // eslint-disable-next-line unicorn/no-nested-ternary
    ...(companyId ? { companyId } : userId ? { userId } : { userId: '' }),
    enabled: !!companyId || !!userId,
  });

  const canViewList = useMemo(() => {
    const enable = documentsInfo?.actions.includes(DocumentAction.LIST);
    return enable;
  }, [documentsInfo?.actions]);

  const [sorting, setSorting] = useState<SortingState>(DEFAULT_SORT);

  const [columnVisibilityState, setColumnVisibilityState] = useState<
    Props['columnVisibility']
  >({
    // Default all columns to false visibility
    ...Object.fromEntries(
      Object.keys(DocumentListColumnHeaders).map((current) => [current, false]),
    ),
    // Enable columns from consumer
    ...columnVisibility,
  });

  const [columnOrder, setColumnOrder] = useState<ColumnOrderState>(
    columnOrderOverride || [],
  );

  const { columns } = useDocumentsTableConfig({
    columnsOverride,
  });

  const tableOptions: Omit<TableOptions<DocumentItem>, 'data' | 'columns'> = {
    state: {
      columnVisibility: columnVisibilityState,
      columnOrder,

      sorting,
    },
    getCoreRowModel: getCoreRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    onColumnVisibilityChange: setColumnVisibilityState,
    onColumnOrderChange: setColumnOrder,
    getPaginationRowModel: getPaginationRowModel(),
    ...optionsOverride,
  };

  const table = useReactTable({
    data: isLoading ? LOADING_ARRAY : (documentsInfo?.items ?? EMPTY_ARRAY),
    columns,
    ...tableOptions,
  });

  return canViewList || isLoading ? (
    <Table
      id="document-list"
      table={table}
      loading={isLoading}
      showErrorState={isError}
      useSorting
      usePagination
      onRowClick={
        onRowClick
          ? (row: DocumentItem) => {
              !isLoading && !isError && onRowClick(row);
            }
          : undefined
      }
      emptyStateProps={emptyStateProps}
      useRowSelection={useRowSelection}
      tableActions={tableActions}
      showHeader={showHeader}
      filterSection={filterSection}
    />
  ) : (
    <Notification
      id="notification-document-list"
      title={NO_RIGHTS_MESSAGE}
      intent="warning"
    />
  );
};
